// Design Time Layer - Templates created from Meta-Design frameworks

export interface DesignTimeTemplate {
  id: string;
  name: string;
  description: string;
  basedOn: string; // encodedId from masterRegistryEntries
  basedOnName: string; // canonical name for display
  templateType: string; // Workshop, Assessment, Playbook, etc.
  deliverableType: string;
  elements: string[];
  status: 'draft' | 'ready' | 'in-use';
  version: number; // Version number for display control
  isEmbedded: boolean; // Whether this should be embedded in M2 models vs standalone
}

export const designTimeTemplates: DesignTimeTemplate[] = [
  // Based on Five Stages of DEGAP
  {
    id: 'dt-001',
    name: 'DEGAP Workshop Design (2-day)',
    description: 'Two-day workshop template that guides teams through all five stages of DEGAP',
    basedOn: 'pf.degap.stages',
    basedOnName: 'Five Stages of DEGAP',
    templateType: 'Workshop Design',
    deliverableType: 'Facilitator Guide + Participant Workbook',
    elements: [
      'Day 1 Morning: Decide - Framing the prioritization challenge',
      'Day 1 Afternoon: Engage - Stakeholder alignment session',
      'Day 1 Evening: Gather - Data collection homework',
      'Day 2 Morning: Arrange - Organizing and structuring priorities',
      'Day 2 Afternoon: Prioritize - Final ranking and commitment'
    ],
    status: 'in-use',
    version: 1,
    isEmbedded: false
  },
  {
    id: 'dt-002',
    name: 'DEGAP Quick Start Guide',
    description: 'Single-page reference guide for teams new to DEGAP',
    basedOn: 'pf.degap.stages',
    basedOnName: 'Five Stages of DEGAP',
    templateType: 'Guide',
    deliverableType: 'PDF Reference Card',
    elements: [
      'One-paragraph overview of each stage',
      'Key questions for each stage',
      'Common pitfalls and how to avoid them',
      'Recommended time allocation'
    ],
    status: 'ready',
    version: 1,
    isEmbedded: false
  },

  // Based on Six Kinds of Items
  {
    id: 'dt-003',
    name: 'Six Kinds Assessment Template',
    description: 'Diagnostic tool to assess prioritization maturity across all six kinds of items',
    basedOn: 'pf.items.kinds',
    basedOnName: 'Six Kinds of Items',
    templateType: 'Assessment',
    deliverableType: 'Interactive Questionnaire',
    elements: [
      'People: 5-10 diagnostic questions',
      'Places: 5-10 diagnostic questions',
      'Things: 5-10 diagnostic questions',
      'Activities: 5-10 diagnostic questions',
      'Information: 5-10 diagnostic questions',
      'Time: 5-10 diagnostic questions',
      'Scoring rubric and interpretation guide'
    ],
    status: 'in-use'
  },
  {
    id: 'dt-004',
    name: 'Six Kinds Workshop Canvas',
    description: 'Visual workshop template for mapping priorities across all six kinds of items',
    basedOn: 'pf.items.kinds',
    basedOnName: 'Six Kinds of Items',
    templateType: 'Canvas / Visual Workspace',
    deliverableType: 'Miro/Mural Template',
    elements: [
      'Six-section canvas layout',
      'Sticky note templates for each domain',
      'Cross-domain relationship mapping area',
      'Priority weighting matrix'
    ],
    status: 'ready',
    version: 1,
    isEmbedded: false
  },

  // Based on 12 Capability Dimensions
  {
    id: 'dt-005',
    name: 'Individual Prioritization Assessment',
    description: 'Personal assessment measuring strength across all 12 capabilities',
    basedOn: 'pf.framework.diagnostic.capabilities.12',
    basedOnName: 'Capability Dimensions (12)',
    templateType: 'Assessment',
    deliverableType: 'Online Assessment App',
    elements: [
      '60 questions (5 per capability)',
      'Likert scale responses',
      'Radar chart visualization',
      'Personalized development recommendations',
      'Capability family groupings',
      'Self vs System orientation analysis'
    ],
    status: 'in-use'
  },
  {
    id: 'dt-006',
    name: 'Team Capability Development Playbook',
    description: 'Structured playbook for improving team capabilities over 90 days',
    basedOn: 'pf.framework.diagnostic.capabilities.12',
    basedOnName: 'Capability Dimensions (12)',
    templateType: 'Playbook',
    deliverableType: '90-Day Action Plan',
    elements: [
      'Baseline assessment protocol',
      '12 capability-building exercises',
      'Weekly check-in templates',
      'Progress tracking dashboard',
      'Quarterly reflection guide'
    ],
    status: 'draft',
    version: 1,
    isEmbedded: false
  },

  // Based on Contextual Modes (STARS)
  {
    id: 'dt-007',
    name: 'STARS Context Assessment',
    description: 'Quick diagnostic to identify which contextual mode a team is operating in',
    basedOn: 'pf.framework.diagnostic.context.modes',
    basedOnName: 'Contextual Modes (STARS)',
    templateType: 'Assessment',
    deliverableType: 'Decision Tree Worksheet',
    elements: [
      'Context identification questionnaire',
      'Mode-specific prioritization guidance',
      'Transition detection indicators',
      'Mode-appropriate tool recommendations'
    ],
    status: 'ready',
    version: 1,
    isEmbedded: false
  },

  // Based on Tool Function Families
  {
    id: 'dt-008',
    name: 'Tool Selection Framework',
    description: 'Guide for selecting the right prioritization tool based on function family needs',
    basedOn: 'gf.tools.families',
    basedOnName: 'Tool Function Families',
    templateType: 'Framework Guide',
    deliverableType: 'Decision Matrix + Tool Library',
    elements: [
      'Function family needs assessment',
      'Tool mapping to each family',
      'Selection decision tree',
      'Implementation readiness checklist'
    ],
    status: 'in-use'
  },

  // Based on Four-Level Diagramming Taxonomy
  {
    id: 'dt-009',
    name: 'Strategy Visualization Template Set',
    description: 'Four-level template set for visualizing strategy from vision to implementation',
    basedOn: 'gf.diagram.levels',
    basedOnName: 'Four-Level Diagramming Taxonomy (The Fourfold Stack)',
    templateType: 'Template Set',
    deliverableType: 'PowerPoint/Figma Template Pack',
    elements: [
      'Vision Map template',
      'Architecture Map template',
      'Enhanced Process Flow Map template',
      'Implementation Blueprint template',
      'Cross-level consistency guide'
    ],
    status: 'ready',
    version: 1,
    isEmbedded: false
  },

  // Based on 5W+Extent
  {
    id: 'dt-010',
    name: 'Project Scoping Worksheet',
    description: 'Comprehensive worksheet using interrogative dimensions to scope any project',
    basedOn: 'idf.core.set',
    basedOnName: 'Interrogative Dimensions Framework (5W+Extent)',
    templateType: 'Worksheet',
    deliverableType: 'Structured Template',
    elements: [
      'Who: Stakeholder identification matrix',
      'What: Scope and deliverables definition',
      'When: Timeline and milestone planning',
      'Where: Context and constraints mapping',
      'Why: Purpose and success criteria',
      'How: Approach and methodology',
      'To-What-Extent: Scale, depth, and boundaries'
    ],
    status: 'in-use'
  },

  // Based on Four Modes of Work
  {
    id: 'dt-011',
    name: 'Mode-Based Meeting Agenda Templates',
    description: 'Four distinct meeting templates optimized for each mode of work',
    basedOn: 'pf.modes.work',
    basedOnName: 'Four Modes of Work',
    templateType: 'Meeting Templates',
    deliverableType: 'Agenda Template Set',
    elements: [
      'Strategy Mode: Vision alignment and direction setting',
      'Prioritization Mode: Ranking and tradeoff discussions',
      'Planning Mode: Resource allocation and sequencing',
      'Delivery Mode: Execution tracking and blockers'
    ],
    status: 'ready',
    version: 1,
    isEmbedded: false
  },

  // Based on Capability Families
  {
    id: 'dt-012',
    name: 'Team Development Curriculum',
    description: '12-week training program organized by capability families',
    basedOn: 'pf.framework.diagnostic.capabilities.families',
    basedOnName: 'Capability Families (4)',
    templateType: 'Course Syllabus',
    deliverableType: 'Training Program',
    elements: [
      'Weeks 1-3: Contextual Awareness modules',
      'Weeks 4-6: Execution Rhythms modules',
      'Weeks 7-9: Relational Navigation modules',
      'Weeks 10-12: Motivational Activation modules',
      'Weekly exercises and assessments',
      'Capstone integration project'
    ],
    status: 'draft',
    version: 1,
    isEmbedded: false
  },

  // Version 3 Templates - Advanced Models (Embedded in M2)
  {
    id: 'dt-v3-001',
    name: 'Johari Window Model',
    description: 'Four-quadrant model for understanding self-awareness and interpersonal communication',
    basedOn: 'pf.awareness.johari',
    basedOnName: 'Johari Window Framework',
    templateType: 'Assessment Model',
    deliverableType: 'Interactive Model',
    elements: [
      'Open Area: Known to self and others',
      'Blind Spot: Unknown to self, known to others',
      'Hidden Area: Known to self, unknown to others',
      'Unknown Area: Unknown to both self and others'
    ],
    status: 'ready',
    version: 3,
    isEmbedded: true
  },
  {
    id: 'dt-v3-002',
    name: 'Logical Levels Model',
    description: 'Six-level hierarchy for understanding change and development',
    basedOn: 'pf.awareness.logical',
    basedOnName: 'Logical Levels Framework',
    templateType: 'Assessment Model',
    deliverableType: 'Interactive Model',
    elements: [
      'Environment: Where and when',
      'Behavior: What',
      'Capabilities: How',
      'Beliefs/Values: Why',
      'Identity: Who',
      'Spirituality: For whom/for what'
    ],
    status: 'ready',
    version: 3,
    isEmbedded: true
  },
  {
    id: 'dt-v3-003',
    name: 'Ladder of Inference Model',
    description: 'Seven-step model for understanding how we form beliefs and take action',
    basedOn: 'pf.awareness.ladder',
    basedOnName: 'Ladder of Inference Framework',
    templateType: 'Assessment Model',
    deliverableType: 'Interactive Model',
    elements: [
      'Observable Data: What we can see and hear',
      'Selected Data: What we choose to focus on',
      'Added Meaning: Our interpretations',
      'Assumptions: What we assume to be true',
      'Conclusions: What we decide',
      'Beliefs: What we believe to be true',
      'Actions: What we do based on our beliefs'
    ],
    status: 'ready',
    version: 3,
    isEmbedded: true
  },

  // Version 2 Templates - Outcome Frameworks
  {
    id: 'dt-v2-001',
    name: 'Desired Outcome Framework',
    description: 'Structured approach to defining and achieving desired outcomes',
    basedOn: 'pf.outcomes.desired',
    basedOnName: 'Desired Outcome Framework',
    templateType: 'Outcome Framework',
    deliverableType: 'Framework Template',
    elements: [
      'Outcome Definition: Clear, specific, measurable',
      'Success Criteria: How you know you\'ve achieved it',
      'Current State Assessment: Where you are now',
      'Gap Analysis: What needs to change',
      'Action Planning: Specific steps to take',
      'Progress Tracking: How to monitor progress'
    ],
    status: 'ready',
    version: 2,
    isEmbedded: false
  },
  {
    id: 'dt-v2-002',
    name: 'Outcome Drivers Framework',
    description: 'Systematic approach to identifying and leveraging outcome drivers',
    basedOn: 'pf.outcomes.drivers',
    basedOnName: 'Outcome Drivers Framework',
    templateType: 'Outcome Framework',
    deliverableType: 'Framework Template',
    elements: [
      'Driver Identification: What influences outcomes',
      'Driver Analysis: Impact and influence assessment',
      'Driver Prioritization: Which drivers matter most',
      'Driver Activation: How to leverage key drivers',
      'Driver Monitoring: Tracking driver effectiveness',
      'Driver Optimization: Continuous improvement'
    ],
    status: 'ready',
    version: 2,
    isEmbedded: false
  }
];

export const getTemplatesBySource = (encodedId: string) => {
  return designTimeTemplates.filter(template => template.basedOn === encodedId);
};

export const getTemplatesByVersion = (version: number) => {
  return designTimeTemplates.filter(template => template.version === version);
};

export const getEmbeddedTemplates = () => {
  return designTimeTemplates.filter(template => template.isEmbedded);
};

export const getStandaloneTemplates = () => {
  return designTimeTemplates.filter(template => !template.isEmbedded);
};

export const getTemplatesByVersionAndEmbedded = (version: number, isEmbedded: boolean) => {
  return designTimeTemplates.filter(template => 
    template.version === version && template.isEmbedded === isEmbedded
  );
};

export const templateTypeColors: Record<string, string> = {
  'Workshop Design': 'sage',
  'Assessment': 'blue',
  'Guide': 'purple',
  'Playbook': 'orange',
  'Canvas / Visual Workspace': 'pink',
  'Framework Guide': 'indigo',
  'Template Set': 'teal',
  'Worksheet': 'cyan',
  'Meeting Templates': 'lime',
  'Course Syllabus': 'amber',
  'Assessment Model': 'violet',
  'Outcome Framework': 'emerald'
};
